import { InAppContentDefinition, InAppDestination } from 'modules/graphql';
import { ValidationError } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { useEffect } from 'react';
import { NegativeFill } from 'ui/icons';
import { AppAndBrowser } from 'ui/icons/devices/AppAndBrowser';
import { TV } from 'ui/icons/devices/TV';
import { TabGroup } from 'ui/tabs';
import { colors } from 'ui/theme';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { Divider, StyledSVGContainer, StyledTab, TabsContainer } from './styles';
import { TABS } from './types';

type Props = {
  selectedTab: TABS;
  setSelectedTab: (tab: TABS) => void;
};

export const InAppTabs = ({ selectedTab, setSelectedTab }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const inAppMediumData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { localeContentErrors } = useErrorValidationContext();

  const appAndBrowserFieldErrors: ValidationError[] = [];
  const rokuFieldErrors: ValidationError[] = [];
  localeContentErrors.forEach((error) =>
    error.isRoku ? rokuFieldErrors.push(error) : appAndBrowserFieldErrors.push(error),
  );

  const hasAppAndBrowserTarget =
    inAppMediumData?.destinations?.includes(InAppDestination.Browser) &&
    inAppMediumData?.destinations?.includes(InAppDestination.Mobile);
  const hasRokuTarget = inAppMediumData?.destinations?.includes(InAppDestination.Roku);

  const appAndBrowserSelected = selectedTab === TABS.APP_AND_BROWSER;
  const rokuSelected = selectedTab === TABS.ROKU;

  const showErrorIcon = (errors: ValidationError[]) =>
    errors.length > 0 ? <NegativeFill color={colors.red100} height={24} width={24} /> : null;

  const getTabLabel = (tab: TABS) => {
    if (tab === TABS.APP_AND_BROWSER)
      return SETTINGS_DISPLAY_CONTENT.destinations.appAndBrowser.title;
    if (tab === TABS.ROKU) return SETTINGS_DISPLAY_CONTENT.destinations.roku.title;
    return '';
  };

  useEffect(() => {
    if (hasRokuTarget && !hasAppAndBrowserTarget) setSelectedTab(TABS.ROKU);
    if (hasAppAndBrowserTarget && !hasRokuTarget) setSelectedTab(TABS.APP_AND_BROWSER);
  }, [hasRokuTarget, hasAppAndBrowserTarget]);

  return (
    <TabsContainer>
      <TabGroup selectedTab={selectedTab} onTabChange={setSelectedTab}>
        {hasAppAndBrowserTarget && (
          <StyledTab id="appAndBrowser" isSelected={appAndBrowserSelected} squareTheme>
            <StyledSVGContainer>
              {showErrorIcon(appAndBrowserFieldErrors)}
              <AppAndBrowser
                color={appAndBrowserSelected ? colors.purple100 : colors.secondaryText}
              />
              {getTabLabel(TABS.APP_AND_BROWSER)}
            </StyledSVGContainer>
          </StyledTab>
        )}
        {hasRokuTarget && (
          <StyledTab id="roku" isSelected={rokuSelected} squareTheme>
            <StyledSVGContainer>
              {showErrorIcon(rokuFieldErrors)}
              <TV color={rokuSelected ? colors.purple100 : colors.secondaryText} />
              {getTabLabel(TABS.ROKU)}
            </StyledSVGContainer>
          </StyledTab>
        )}
      </TabGroup>
      {(hasAppAndBrowserTarget || hasRokuTarget) && <Divider />}
    </TabsContainer>
  );
};
